import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Loader2, Pencil, Printer, RefreshCw } from 'lucide-react'

import { api } from '@/api/client'
import type { PurchaseRegisterLine, PurchaseRegisterResponse, Supplier } from '@/types/models'
import { Field, inputCls } from '@/components/Field'
import { Modal } from '@/components/Modal'
import { cn, formatNum, isLargeQty } from '@/lib/utils'
import { useFileDownload } from '@/lib/useFileDownload'
import { useTabField } from '@/stores/tabState'
import { useToast } from '@/stores/toast'

interface NoteEdit {
  item_id: number
  item_name: string
  partner_id: number
  partner_name: string
  scope: 'common' | 'partner'
  note: string
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/** 전사통합 구매장(보기) — 그날 전 회사 출고를 구매장용/라벨용 품목 기준으로 묶어 표시.
 *  source='label' 이면 라벨용 매핑 기준 + 라벨 포장단위 열 표시. */
// 'YYYY-MM-DD' → '2026년 10월 13일 화요일' (원본 구매장 날짜 줄)
function printDate(iso: string) {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y) return iso
  return `${y}년 ${m}월 ${d}일 ${'일월화수목금토'[new Date(y, m - 1, d).getDay()]}요일`
}

// ── 구매장 인쇄 도우미 ─────────────────────────────────────────────
type RegRow = { item: string; line: PurchaseRegisterLine; groupStart: boolean }

/** 구매장(야채) 인쇄: 전체 줄을 가운데에 가장 가까운 품목 경계에서 반으로 → [왼쪽, 오른쪽] 행 쌍.
 *  엑셀(register_xlsx._split_half)과 같은 규칙 — 한 품목이 마지막 장 왼쪽↔첫 장 오른쪽으로 갈리지 않게. */
function halfRows(groups: { purchase_item: string; lines: PurchaseRegisterLine[] }[]): [RegRow | null, RegRow | null][] {
  const rows: RegRow[] = groups.flatMap((g) => g.lines.map((line, i) => ({ item: g.purchase_item, line, groupStart: i === 0 })))
  const mid = Math.ceil(rows.length / 2)
  const starts = rows.map((r, i) => (r.groupStart && i > 0 ? i : -1)).filter((i) => i > 0)
  const cut = starts.length
    ? starts.reduce((b, x) => (Math.abs(x - mid) < Math.abs(b - mid) || (Math.abs(x - mid) === Math.abs(b - mid) && x > b) ? x : b))
    : mid
  const L = rows.slice(0, cut), R = rows.slice(cut)
  return Array.from({ length: Math.max(L.length, R.length) }, (_, i) => [L[i] ?? null, R[i] ?? null])
}

/** 실제 글자 폭(mm) — 화면 글꼴로 canvas 에서 잰다(인쇄 글꼴과 같음). */
let _measureCtx: CanvasRenderingContext2D | null = null
function textMm(t: string, pt: number) {
  if (!_measureCtx) {
    _measureCtx = document.createElement('canvas').getContext('2d')
    if (!_measureCtx) return t.length * pt * 0.3528
  }
  _measureCtx.font = `${pt}pt ${getComputedStyle(document.body).fontFamily}`
  return (_measureCtx.measureText(t).width * 25.4) / 96     // CSS px → mm
}

/** 칸 너비(mm)에 들어가도록 글씨 크기(pt) — 들어가면 기본 크기 그대로(엑셀 '셀에 맞춤'). */
function fitStyle(t: string, widthMm: number, basePt: number): React.CSSProperties | undefined {
  if (!t) return undefined
  const avail = widthMm - 2.2                                // 칸 여백·테두리
  const need = textMm(t, basePt)
  if (need <= avail) return undefined
  return { fontSize: `${Math.max(5, (basePt * avail) / need).toFixed(2)}pt` }
}

// 칸 너비(mm): 구매장 A4−여백2mm 반쪽 ≈103mm, 과일 A4−여백8mm−단간격 반쪽 ≈100mm
const REG_MM = {
  veg: { item: 20.6, note: 22.6, partner: 38.9, pt: 10 },
  fruit: { item: 20.0, note: 22.0, partner: 38.0, pt: 11 },
}

function RegCols() {
  return (
    <colgroup>
      <col style={{ width: '20%' }}/><col style={{ width: '22%' }}/><col style={{ width: '11%' }}/>
      <col style={{ width: '9%' }}/><col/>
    </colgroup>
  )
}

function RegCells({ mode, item, line: l, groupTop }: {
  mode: 'veg' | 'fruit'; item: string; line: PurchaseRegisterLine; groupTop?: boolean
}) {
  const w = REG_MM[mode]
  const gt = groupTop ? 'gt' : ''      // 품목이 바뀌는 줄 윗선
  return (
    <>
      <td className={cn('lb', gt)} style={fitStyle(item, w.item, w.pt)}>{item}</td>
      <td className={cn('text-right text-red-600', gt)} style={fitStyle(l.note_all ?? '', w.note, w.pt)}>{l.note_all}</td>
      <td className={cn('text-right tabular-nums', gt, l.shade && 'shade')}>{formatNum(l.qty)}</td>
      <td className={cn('text-center', gt)}>{l.unit}</td>
      <td className={cn('rb', gt)} style={fitStyle(l.partner_name, w.partner, w.pt)}>{l.partner_name}</td>
    </>
  )
}

function HalfCells({ row }: { row: RegRow | null }) {
  if (!row) return <><td className="lb"/><td/><td/><td/><td className="rb"/></>
  return (
    <RegCells mode="veg" item={row.groupStart ? row.item : ''} line={row.line} groupTop={row.groupStart}/>
  )
}

export function PurchaseRegisterAllTab({ source = 'purchase' }: { source?: 'purchase' | 'label' }) {
  const [date, setDate] = useTabField(`purchase-register-${source}`, 'date', today())
  const isLabel = source === 'label'
  const { downloading, download } = useFileDownload()
  // 라벨: 출력 묶음(회사 정보의 '라벨 출력 묶음') — 기본 첫 묶음, '' = 전체 회사.
  const { data: labelGroups = [] } = useQuery({
    queryKey: ['purchase-label-groups'],
    queryFn: () => api<{ label_group: string; companies: string[] }[]>('/api/purchase/label-groups'),
    enabled: isLabel,
  })
  const [pickedGroup, setGroup] = useTabField<string | null>(`purchase-register-${source}`, 'labelGroup', null)
  const labelGroup = isLabel ? (pickedGroup ?? labelGroups[0]?.label_group ?? '') : ''
  // 구매장: 야채(전 품목) | 과일(C 구역만, 268호 제외). 라벨: 협력업체 필터(상동 등에 보낼 라벨).
  const [section, setSection] = useTabField<'' | 'fruit'>(`purchase-register-${source}`, 'section', '')
  const [supplierId, setSupplierId] = useTabField<string>(`purchase-register-${source}`, 'supplier', '')
  const { data: suppliers = [] } = useQuery({
    queryKey: ['purchase-suppliers'],
    queryFn: () => api<Supplier[]>('/api/purchase/suppliers'),
    enabled: isLabel,
  })
  const isFruit = !isLabel && section === 'fruit'
  const groupQs = (labelGroup ? `&label_group=${encodeURIComponent(labelGroup)}` : '')
    + (isFruit ? '&section=fruit' : '')
    + (isLabel && supplierId ? `&supplier=${supplierId}` : '')
  const supplierName = suppliers.find((x) => String(x.id) === supplierId)?.name ?? ''
  const title = isLabel
    ? `라벨${labelGroup ? ` (${labelGroup})` : ''}${supplierName ? ` · ${supplierName}` : ''}`
    : (isFruit ? '과일 구매장' : '구매장')
  const fileBase = isLabel
    ? `라벨${labelGroup ? `_${labelGroup}` : ''}${supplierName ? `_${supplierName}` : ''}`
    : (isFruit ? '과일구매장' : '구매장')
  const { data, isFetching, refetch } = useQuery({
    queryKey: ['purchase-register-all', source, date, groupQs],
    queryFn: () => api<PurchaseRegisterResponse>(`/api/purchase/register?date=${date}&source=${source}${groupQs}`),
    enabled: !!date && (!isLabel || pickedGroup !== null || labelGroups.length > 0),
  })

  // 구매장 보기에서 특이사항 바로 수정 — 이 품목 공통 / 이 거래처만.
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const [noteEdit, setNoteEdit] = useState<NoteEdit | null>(null)
  const saveNote = useMutation({
    mutationFn: (e: NoteEdit) => api('/api/purchase/line-note', {
      method: 'POST',
      body: { item_id: e.item_id, scope: e.scope, partner_id: e.partner_id, note: e.note.trim() },
    }),
    onSuccess: () => {
      for (const k of ['purchase-register-all', 'purchase-item-maps', 'purchase-name-order']) {
        qc.invalidateQueries({ queryKey: [k] })
      }
      setNoteEdit(null)
      push('특이사항 저장됨', 'success')
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 6000),
  })
  function openNote(l: PurchaseRegisterLine) {
    setNoteEdit({
      item_id: l.item_id, item_name: l.item_name,
      partner_id: l.partner_id, partner_name: l.partner_name,
      scope: l.note_scope === 'partner' ? 'partner' : 'common', note: l.note ?? '',
    })
  }
  const firstUnordered = data?.groups.findIndex((g) => !g.ordered) ?? -1

  return (
    <div id="pur-reg-print" className="max-w-6xl mx-auto p-6">
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #pur-reg-print, #pur-reg-print * { visibility: visible !important; }
        #pur-reg-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
        #pur-reg-print .no-print { display: none !important; }
        #pur-reg-print table { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        @page { size: A4 portrait; margin: ${isLabel ? '10mm' : isFruit ? '6mm 4mm' : '3mm 1mm'}; }
        #pur-reg-print, #pur-reg-print * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        #pur-reg-print .reg table { width: 100%; table-layout: fixed; border-collapse: collapse; }
        #pur-reg-print .reg td { border-bottom: 1px dotted #888; padding: 0 2px; white-space: nowrap;
          overflow: hidden; text-overflow: clip; line-height: 1; }
        #pur-reg-print .reg tr { break-inside: avoid; }
        #pur-reg-print .reg td.shade { background: #bfbfbf; }
        #pur-reg-print .reg td.lb { border-left: 3px double #000; }
        #pur-reg-print .reg td.rb { border-right: 3px double #000; }
        #pur-reg-print .reg.veg td { height: 4.8mm; font-size: 10pt; }
        #pur-reg-print .reg.veg td.gt { border-top: 1px solid #000; }
        #pur-reg-print .reg.veg td.gap { border-bottom: 0; }
        #pur-reg-print .reg.fruit { column-count: 2; column-gap: 1.5mm; }
        #pur-reg-print .reg.fruit td { height: 5.8mm; font-size: 11pt; }
        #pur-reg-print .reg.fruit table { break-inside: avoid; border-top: 2px solid #0070c0; }
      }`}</style>
      {isLabel
        ? <h3 className="hidden print:block text-lg font-bold mb-3">{title} — {date}</h3>
        : (
          <div className="hidden print:block mb-0.5">
            <div className={cn('text-center font-bold text-[14pt] tracking-[0.6em]',
                               isFruit ? 'bg-[#ff66cc]' : 'bg-[#00ff99]')}>{title}</div>
            <div className="text-[12pt]">{printDate(date)}</div>
          </div>
        )}
      <div className="no-print bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm mb-3">
        <label className="text-xs font-semibold text-slate-500">일자</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
               className={inputCls + ' w-44 tabular-nums'}/>
        {/* 그사이 출고가 바뀌었으면 다시 조회(같은 날짜를 다시 골라도 캐시를 보여 주므로) */}
        <button onClick={() => refetch()} disabled={!date || isFetching}
                className="flex items-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-semibold rounded-lg px-3 py-2 disabled:opacity-40">
          <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')}/> 조회
        </button>
        {!isLabel && (
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm">
            {([['', '야채 구매장'], ['fruit', '과일 구매장']] as const).map(([k, lbl]) => (
              <button key={k} onClick={() => setSection(k)}
                      className={cn('px-3 py-1.5 font-semibold',
                        section === k ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50')}>
                {lbl}
              </button>
            ))}
          </div>
        )}
        {isLabel && (
          <>
            <label className="text-xs font-semibold text-slate-500">협력업체</label>
            <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={inputCls + ' w-36'}>
              <option value="">전체</option>
              {suppliers.filter((x) => x.kind === 'supplier').map((x) => (
                <option key={x.id} value={String(x.id)}>{x.name}</option>
              ))}
            </select>
            <label className="text-xs font-semibold text-slate-500">출력 묶음</label>
            <select value={labelGroup} onChange={(e) => setGroup(e.target.value)}
                    title={labelGroups.find((g) => g.label_group === labelGroup)?.companies.join(', ') ?? '전 회사'}
                    className={inputCls + ' w-56'}>
              {labelGroups.map((g) => (
                <option key={g.label_group} value={g.label_group}>{g.label_group}</option>
              ))}
              <option value="">전체 회사</option>
            </select>
          </>
        )}
        <span className="ml-auto text-xs text-slate-400 tabular-nums">
          {data ? `${data.group_count}개 품목 · 총 ${formatNum(data.grand_qty)}` : (isFetching ? '조회 중…' : '')}
        </span>
        <button onClick={() => window.print()} disabled={!data?.groups.length}
                className="flex items-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
          <Printer className="w-4 h-4"/> 인쇄
        </button>
        <button onClick={() => download(
                  `/api/purchase/register/export?date=${date}&source=${source}${groupQs}`,
                  `${fileBase}_${date}.xlsx`)}
                disabled={downloading || !data?.groups.length}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          엑셀 다운로드
        </button>
      </div>

      {/* 라벨 인쇄용 — 엑셀과 동일(업장명 | 품명 | 수량 | 단위 | 원산지 | 보관방법 | 특이사항 | 포장단위) */}
      {isLabel && data && !!data.groups.length && (
        <table className="hidden print:table w-full text-xs border border-slate-500 border-collapse [&_td]:border [&_td]:border-slate-400 [&_th]:border [&_th]:border-slate-500 [&_td]:px-2 [&_td]:py-1 [&_th]:px-2 [&_th]:py-1">
          <thead>
            <tr>
              <th className="text-left">업장명</th>
              <th className="text-left">품명</th>
              <th className="text-right">수량</th>
              <th className="text-left">단위</th>
              <th className="text-left">원산지</th>
              <th className="text-left">보관방법</th>
              <th className="text-left">특이사항</th>
              <th className="text-left">포장단위</th>
            </tr>
          </thead>
          <tbody>
            {data.groups.map((g) => g.lines.map((l, i) => (
              <tr key={`${g.purchase_item}-${i}`}>
                <td>{l.partner_name}</td>
                <td>{g.purchase_item}</td>
                <td className="text-right tabular-nums">{formatNum(l.qty)}</td>
                <td>{l.unit}</td>
                <td>{l.origin}</td>
                <td>{l.storage}</td>
                <td className="text-rose-600">{l.note_all}</td>
                <td>{l.label_pack_unit}</td>
              </tr>
            )))}
          </tbody>
        </table>
      )}

      {/* 구매장 인쇄 — 엑셀과 같은 배치: 전체를 반으로 잘라 앞 절반 왼쪽·뒤 절반 오른쪽(같은 행),
          페이지는 브라우저가 나눔. 과일: 신문식 2단, 묶음은 안 쪼갬, 묶음 사이 파란 굵은 선.
          칸보다 긴 글자는 그 칸만 글씨를 줄임(엑셀 '셀에 맞춤'). */}
      {!isLabel && data && !!data.groups.length && (isFruit ? (
        <div className="hidden print:block reg fruit">
          {data.groups.map((g) => (
            <table key={g.purchase_item}>
              <RegCols/>
              <tbody>
                {g.lines.map((l, i) => (
                  <tr key={i}>
                    <RegCells mode="fruit" item={i === 0 ? g.purchase_item : ''} line={l}/>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        </div>
      ) : (
        <div className="hidden print:block reg veg">
          <table>
            <colgroup>
              <col style={{ width: '9.9%' }}/><col style={{ width: '10.9%' }}/><col style={{ width: '5.5%' }}/>
              <col style={{ width: '4.5%' }}/><col style={{ width: '18.8%' }}/>
              <col style={{ width: '0.8%' }}/>
              <col style={{ width: '9.9%' }}/><col style={{ width: '10.9%' }}/><col style={{ width: '5.5%' }}/>
              <col style={{ width: '4.5%' }}/><col style={{ width: '18.8%' }}/>
            </colgroup>
            <tbody>
              {halfRows(data.groups).map(([L, R], i) => (
                <tr key={i}>
                  <HalfCells row={L}/>
                  <td className="gap"/>
                  <HalfCells row={R}/>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {data && (
        <div className="space-y-4 print:hidden">
          {data.groups.map((g, gi) => (
            <Fragment key={g.purchase_item}>
            {gi === firstUnordered && (
              <div className="flex items-center gap-3 pt-2 text-xs font-semibold text-amber-700">
                <span className="h-px flex-1 bg-amber-300"/>
                정렬 미지정 품목 — 구매장 &gt; 품명 정렬 탭에서 순서를 지정하세요
                <span className="h-px flex-1 bg-amber-300"/>
              </div>
            )}
            <div className={cn('bg-white rounded-xl border overflow-hidden',
                               g.ordered ? 'border-slate-200' : 'border-amber-300')}>
              <div className={cn('flex items-center gap-2 px-4 py-2 border-b',
                                 g.ordered ? 'bg-slate-50 border-slate-200' : 'bg-amber-50 border-amber-200')}>
                <span className="font-semibold text-slate-900">{g.purchase_item}</span>
                {!g.ordered && (
                  <span className="text-[11px] font-semibold text-amber-700 bg-white border border-amber-300 rounded px-1.5 py-0.5">
                    정렬 미지정
                  </span>
                )}
                <span className="ml-auto text-sm font-bold tabular-nums text-emerald-700">
                  {g.total_label ?? formatNum(g.total_qty)}
                </span>
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500">
                  <tr className="border-b border-slate-100">
                    <th className="px-3 py-1.5 text-left font-medium">거래처</th>
                    <th className="px-3 py-1.5 text-left font-medium w-40">특이사항</th>
                    <th className="px-3 py-1.5 text-right font-medium w-20">수량</th>
                    <th className="px-3 py-1.5 text-left font-medium w-14">단위</th>
                    {isLabel && <th className="px-3 py-1.5 text-left font-medium w-20">원산지</th>}
                    {isLabel && <th className="px-3 py-1.5 text-left font-medium w-36">보관방법</th>}
                    {isLabel && <th className="px-3 py-1.5 text-left font-medium w-28">라벨 포장단위</th>}
                  </tr>
                </thead>
                <tbody>
                  {g.lines.map((l, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="px-3 py-1.5 text-slate-800">{l.partner_name}</td>
                      {isLabel ? (
                        <td className="px-3 py-1.5 text-rose-600 text-xs">{l.note_all}</td>
                      ) : (
                        <td className="px-3 py-1.5 text-xs">
                          <button type="button" onClick={() => openNote(l)} title="특이사항 수정"
                                  className="group w-full text-left flex items-center gap-1 rounded px-1 -mx-1 py-0.5 hover:bg-rose-50">
                            {/* 소수 팩 환산 줄은 환산된 특이사항(비고2 포함)을 보이고, 연필은 원래 특이사항을 고친다 */}
                            <span className="text-rose-600">{l.converted_from ? l.note_all : l.note}</span>
                            {l.converted_from && (
                              <span title={`원래 ${l.converted_from} → 1팩으로 환산`}
                                    className="whitespace-nowrap text-[10px] font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded px-1">
                                환산 {l.converted_from}
                              </span>
                            )}
                            <Pencil className={cn('w-3 h-3 text-slate-300 group-hover:text-rose-500 shrink-0',
                                                  l.note ? 'opacity-0 group-hover:opacity-100' : '')}/>
                          </button>
                          {l.note2 && !l.converted_from && (
                            <div className="text-rose-600 mt-0.5" title="전표 비고2(이 출고 건 코멘트)">
                              <span className="text-[10px] text-slate-400 mr-1">비고2</span>{l.note2}
                            </div>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {/* 100 이상 = 판매월보와 같은 빨강(오타 확인용, 2026-10-29), 환산 못 한 소수·0 = 회색 */}
                        <span className={cn(isLargeQty(l.qty) ? 'bg-red-200 text-red-700 rounded px-1'
                          : l.shade && 'bg-slate-300 rounded px-1')}>{formatNum(l.qty)}</span>
                      </td>
                      <td className="px-3 py-1.5 text-slate-500 text-xs">{l.unit}</td>
                      {isLabel && <td className="px-3 py-1.5 text-slate-600 text-xs">{l.origin}</td>}
                      {isLabel && (
                        <td className={cn('px-3 py-1.5 text-xs', l.storage === '냉동보관' ? 'text-sky-700 font-medium' : 'text-slate-600')}>
                          {l.storage}
                        </td>
                      )}
                      {isLabel && <td className="px-3 py-1.5 text-slate-600 text-xs">{l.label_pack_unit}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </Fragment>
          ))}
          {!data.groups.length && (
            <div className="text-center py-16 text-slate-400 text-sm">이 날짜의 출고가 없습니다.</div>
          )}
        </div>
      )}

      <Modal open={!!noteEdit} onClose={() => setNoteEdit(null)} title="특이사항 수정" size="md">
        {noteEdit && (
          <form onSubmit={(e) => { e.preventDefault(); saveNote.mutate(noteEdit) }} className="space-y-3">
            <div className="text-sm text-slate-600">
              품목 <span className="font-semibold text-slate-900">{noteEdit.item_name}</span>
              <span className="mx-2 text-slate-300">·</span>
              거래처 <span className="font-semibold text-slate-900">{noteEdit.partner_name}</span>
            </div>
            <Field label="적용 범위" full>
              <div className="space-y-1.5">
                {([
                  ['common', '이 품목 공통', `전 거래처의 '${noteEdit.item_name}'에 적용`],
                  ['partner', '이 거래처만', `'${noteEdit.partner_name}'에만 적용 (공통보다 우선)`],
                ] as const).map(([v, lbl, hint]) => (
                  <label key={v} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="radio" name="note-scope" className="mt-1" checked={noteEdit.scope === v}
                           onChange={() => setNoteEdit({ ...noteEdit, scope: v })}/>
                    <span>
                      <span className="font-medium text-slate-800">{lbl}</span>
                      <span className="block text-xs text-slate-500">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </Field>
            <Field label="특이사항" full>
              <input autoFocus value={noteEdit.note} maxLength={200}
                     onChange={(e) => setNoteEdit({ ...noteEdit, note: e.target.value })}
                     placeholder="비우면 특이사항 삭제" className={inputCls}/>
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setNoteEdit(null)}
                      className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit" disabled={saveNote.isPending}
                      className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40">
                {saveNote.isPending ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
